import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { formatNumber, formatMs, formatDateTime } from '../utils/format';
import {
  Activity,
  Download,
  Search,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';

interface UsageLog {
  id: string;
  tool: string;
  status: 'success' | 'error';
  responseTime: number;
  errorMessage?: string;
  apiKeyName: string;
  createdAt: string;
}

interface UsageStats {
  totalRequests: number;
  successRate: number;
  avgResponseTime: number;
  topTools: Array<{ tool: string; count: number }>;
}

export default function Usage() {
  const [logs, setLogs] = useState<UsageLog[]>([]);
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'success' | 'error'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadUsage();
  }, []);

  async function loadUsage() {
    try {
      const usage = await api.get<{ data: { requests: { used: number; limit: number }; success_rate: number } }>('/api/usage');
      setStats({
        totalRequests: usage.data.requests.used,
        successRate: usage.data.success_rate,
        avgResponseTime: 0,
        topTools: [],
      });
      setLogs([]);
    } catch (error) {
      console.error('Failed to load usage:', error);
      setStats({
        totalRequests: 0,
        successRate: 100,
        avgResponseTime: 0,
        topTools: [],
      });
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }

  const filteredLogs = logs.filter((log) => {
    if (filter !== 'all' && log.status !== filter) return false;
    if (search && !log.tool.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-n2f-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-n2f-text">Usage</h1>
          <p className="text-n2f-text-secondary">Monitor your API requests and performance</p>
        </div>
        <button className="btn btn-secondary">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <Activity className="h-8 w-8 text-n2f-accent" />
          <div className="stat-value mt-4">{formatNumber(stats?.totalRequests || 0)}</div>
          <div className="stat-label">Total Requests</div>
        </div>
        <div className="stat-card">
          <CheckCircle className="h-8 w-8 text-green-400" />
          <div className="stat-value mt-4">{stats?.successRate.toFixed(1)}%</div>
          <div className="stat-label">Success Rate</div>
        </div>
        <div className="stat-card">
          <Clock className="h-8 w-8 text-blue-400" />
          <div className="stat-value mt-4">{formatMs(stats?.avgResponseTime || 0)}</div>
          <div className="stat-label">Avg Response Time</div>
        </div>
      </div>

      {/* Top Tools */}
      <div className="card">
        <h2 className="text-lg font-semibold text-n2f-text mb-4">Top Tools</h2>
        <div className="space-y-3">
          {stats?.topTools.map((tool, index) => (
            <div key={tool.tool} className="flex items-center">
              <span className="w-6 text-sm text-n2f-text-muted">{index + 1}</span>
              <code className="flex-1 text-sm bg-n2f-elevated px-2 py-1 rounded text-n2f-text-secondary">{tool.tool}</code>
              <span className="ml-4 text-sm font-medium text-n2f-text">
                {formatNumber(tool.count)}
              </span>
              <div className="ml-4 w-24 h-2 bg-n2f-elevated rounded-full overflow-hidden">
                <div
                  className="h-full bg-n2f-accent rounded-full"
                  style={{
                    width: `${(tool.count / (stats?.topTools[0]?.count || 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Logs */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-4 sm:space-y-0">
          <h2 className="text-lg font-semibold text-n2f-text">Request Logs</h2>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-n2f-text-muted" />
              <input
                type="text"
                placeholder="Search tools..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-9 py-1.5 w-48"
              />
            </div>
            <div className="flex items-center bg-n2f-elevated rounded-lg p-1">
              {(['all', 'success', 'error'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    filter === f
                      ? 'bg-n2f-card shadow text-n2f-text'
                      : 'text-n2f-text-muted hover:text-n2f-text'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-n2f-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-n2f-text-muted">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-n2f-text-muted">Tool</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-n2f-text-muted">API Key</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-n2f-text-muted">Response Time</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-n2f-text-muted">Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id} className="border-b border-n2f-border last:border-0 hover:bg-n2f-elevated/50">
                  <td className="py-3 px-4">
                    {log.status === 'success' ? (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-400" />
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <code className="text-sm bg-n2f-elevated px-2 py-1 rounded text-n2f-text-secondary">{log.tool}</code>
                    {log.errorMessage && (
                      <p className="text-xs text-red-400 mt-1">{log.errorMessage}</p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm text-n2f-text-secondary">{log.apiKeyName}</td>
                  <td className="py-3 px-4">
                    <span
                      className={`text-sm ${
                        log.responseTime > 1000
                          ? 'text-red-400'
                          : log.responseTime > 500
                          ? 'text-orange-400'
                          : 'text-green-400'
                      }`}
                    >
                      {formatMs(log.responseTime)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-n2f-text-muted">
                    {formatDateTime(log.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
